import { useEffect } from "react";
import { useDispatch, useStore } from "react-redux";
import { customerSearch } from "facade/ServiceFacade";
import {
  askCustomerPhoneNumber,
  setCustomerEnteredNumber,
} from "@rista/core/redux-store/slices/CustomerDisplay";
import {
  setupAskCustomerPhoneNumberListener,
  setupCustomerDisplayListener,
} from "redux-store/middleware/saleUpdate";
import { customerDisplayEffect } from "@rista/core/redux-store/middleware/saleUpdate/customerDisplayEffect";
import {
  answerCartRequests,
  onDisplayMessage,
  openCustomerDisplay,
} from "utils/customerDisplay";

/**
 * The POS window's half of the customer display: claims the second monitor,
 * answers the display's replay requests, and handles what it sends back.
 *
 * Mounted once from Routes.js rather than from the components that trigger
 * these flows — CustomerSearchInput unmounts between orders, and a reply
 * arriving with no listener is simply lost.
 */
export const useCustomerDisplayHost = () => {
  const dispatch = useDispatch();
  const store = useStore();

  // Claim the second monitor if there is one. The shell answers
  // NO_SECOND_SCREEN on single-monitor machines and nothing happens, so this
  // needs no setting — on dual-screen POS hardware the second screen IS the
  // customer display.
  // ponytail: auto-open, no opt-out. Add a PosSettings toggle if an outlet ever
  // wants the second screen for something else.
  useEffect(() => {
    openCustomerDisplay();
  }, []);

  useEffect(() => answerCartRequests(), []);

  // Mirrors the Ask/Cancel toggle onto the display. Registered here (not in
  // NewOrder/CheckoutContainer, which only run their own listeners while
  // mounted) because CustomerSearchInput's Ask button can be reached from
  // either screen, and this hook is the one thing guaranteed mounted for the
  // life of the POS window.
  useEffect(() => setupAskCustomerPhoneNumberListener(), []);

  // Same reason: the sale can be loaded or changed from screens other than
  // NewOrder (MyOrderCart's "Edit items", quick settle), and NewOrder's own
  // listener is not subscribed at that point.
  useEffect(() => setupCustomerDisplayListener(), []);

  // The listeners above only fire on the NEXT change. Push once on mount so a
  // POS window that starts with a sale already in the store — a reload
  // mid-order, a rehydrated cart — hands the display something to replay
  // instead of leaving it blank until the first edit.
  useEffect(() => {
    customerDisplayEffect(null, store);
  }, [store]);

  useEffect(
    () =>
      onDisplayMessage((message) => {
        if (message?.type !== "customerNumber") return;

        // CustomerSearchInput mirrors this into its own phone field so the
        // number the customer typed on the display shows up on the POS side too.
        dispatch(setCustomerEnteredNumber(message.number));

        customerSearch(
          { pageSize: 1, phoneNumber: message.number, phoneCountryCode: "IN" },
          // The prompt is showing on the display; clear it once the POS has the
          // number, whatever the search found.
          () => dispatch(askCustomerPhoneNumber(false)),
        );
      }),
    [dispatch],
  );
};
